/**
 * Social Store — wachtrij, goedkeuring, statistieken en weekplan van het
 * Instagram-systeem (Marketing > Social). Praat met /api/marketing/social/* via marketingFetch.
 */

import { create } from 'zustand';
import { marketingFetch } from './marketing-store';

export type SocialStatus =
  | 'kandidaat'
  | 'afgekeurd'
  | 'gerenderd'
  | 'goedgekeurd'
  | 'gepland'
  | 'gepubliceerd'
  | 'geskipt'
  | 'mislukt';

export type SocialPijler = 'voor-na' | 'mooiste-kleed' | 'achter-de-schermen' | 'klant-aan-het-woord' | 'tips' | 'ugc';
export type SocialHookType = 'vraag' | 'cijfer' | 'contrast' | 'uitspraak' | 'tip' | 'verhaal';

export const PIJLERS: readonly SocialPijler[] = ['voor-na', 'mooiste-kleed', 'achter-de-schermen', 'klant-aan-het-woord', 'tips', 'ugc'];

export interface SocialKaart {
  tekst?: string;
  titel?: string;
  voornaam?: string;
  bron_label?: string;
  score_label?: string;
  bron_url?: string;
  alt?: string;
  url?: string;
}

export interface SocialPost {
  id: string;
  platform: 'instagram' | 'facebook' | 'threads';
  format: 'reel' | 'story' | 'carousel' | 'compilatie' | 'foto';
  status: SocialStatus;
  pijler: SocialPijler;
  hook_type: SocialHookType | null;
  experiment: string | null;
  bron_ref: string | null;
  kaarten: SocialKaart[] | null;
  wi_sf_id: string | null;
  wo_sf_id: string | null;
  media_ids: string[];
  kleed_soort: string | null;
  kleed_afmeting: string | null;
  selectie_score: number | null;
  selectie_detail: {
    reden?: string;
    kleed_omschrijving?: string;
    score_contrast?: number;
    score_kleed?: number;
    score_clip_bruikbaar?: number;
    privacy?: Record<string, unknown>;
    privacy_hit?: string | null;
    waarom?: string;
    hook_suggestie?: string;
    cluster?: string;
  } | null;
  selectie_bron: string | null;
  render_url: string | null;
  cover_url: string | null;
  render_duur_s: number | null;
  caption: string | null;
  hashtags: string[] | null;
  alt_text: string | null;
  caption_origineel: string | null;
  hashtags_origineel: string[] | null;
  caption_bewerkt_door: string | null;
  gepubliceerd_op: string | null;
  permalink: string | null;
  beoordeeld_door: string | null;
  beoordeeld_at: string | null;
  beoordeel_reden: string | null;
  actor_bron: 'brein' | 'office-app';
  fout: string | null;
  pogingen: number;
  created_at: string;
}

export interface SocialPostStats {
  post_id: string;
  snapshot_date: string;
  reach: number | null;
  views: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  saves: number | null;
  follows: number | null;
  avg_watch_ms: number | null;
  total_interactions: number | null;
}

export interface Groep {
  n: number;
  reach: number;
  saves: number;
  shares: number;
}

export interface SocialStatsData {
  klantreis?: {
    from: string;
    clicks: number;
    filtered: number;
    sources: Array<{ source: string; label: string; instagram: number; facebook: number; filtered: number }>;
  } | null;
  account: Record<string, Array<{ platform: string; snapshot_date: string; followers: number | null; reach_dag: number | null; profile_views: number | null; website_clicks: number | null }>>;
  posts: Array<SocialPost & { stats: SocialPostStats | null }>;
  leer: {
    perFormat: Record<string, Groep>;
    perKleedSoort: Record<string, Groep>;
    perUur: Record<string, Groep>;
    perSelectieBron: Record<string, Groep>;
    perPijler: Record<string, Groep>;
    perHookType: Record<string, Groep>;
  };
  wachtrij: Record<string, number>;
  autonoom: { mtd_gepubliceerd: number; mtd_brein: number; pct: number };
}

export interface KandidaatSet {
  wi_sf_id: string;
  wo_sf_id: string | null;
  gemarkeerd: boolean;
  laatste_at: string;
  kleed_soort: string | null;
  kleed_afmeting: string | null;
  item_naam: string | null;
  thumbs: { voor: string | null; tijdens: string | null; na: string | null };
}

export interface KandidatenData {
  sets: KandidaatSet[];
  gemarkeerd: Array<{ id: string; url: string; type: 'foto' | 'video'; fase: string; wi_sf_id: string | null; taken_at: string }>;
}

export interface SocialWeekplanRij {
  pijler: SocialPijler;
  format: 'reel' | 'foto' | 'carousel';
  n: number;
  hook_type?: SocialHookType | null;
}

export interface SocialWeekplan {
  id: string;
  week_start: string;
  plan: Record<string, SocialWeekplanRij[]>;
  experiment: { hypothese: string; pijler: SocialPijler; hook_type: SocialHookType; meetpunt: string; label?: string } | null;
  onderbouwing: string | null;
  cijfers: { correcties?: string[]; boven_drempel?: string[]; voorstel?: Record<string, SocialWeekplanRij[]> } | null;
  stuurt: boolean;
  created_at: string;
}

export interface SocialLeerpunt {
  id: string;
  week_start: string;
  pijler: SocialPijler | null;
  soort: 'plan' | 'caption';
  leerpunt: string;
  bewijs: { tekst?: string; notitie?: string } | null;
  zekerheid: 'laag' | 'middel' | 'hoog';
  status: 'voorgesteld' | 'akkoord' | 'afgewezen' | 'toegepast';
  created_at: string;
}

export interface WeekplanData {
  huidig: SocialWeekplan | null;
  vorig: SocialWeekplan | null;
  leerpunten: SocialLeerpunt[];
}

export interface SocialCaptionBewerking {
  id: string;
  post_id: string;
  pijler: SocialPijler | null;
  format: string | null;
  platform: string | null;
  hook_type: SocialHookType | null;
  status_bij_bewerking: string | null;
  caption_voor: string | null;
  caption_na: string | null;
  hashtags_voor: string[] | null;
  hashtags_na: string[] | null;
  gelijkenis: number | null;
  bewerkt_door: string;
  created_at: string;
}

export interface BewerkingenData {
  dagen: number;
  bewerkingen: SocialCaptionBewerking[];
  per_pijler: Array<{ pijler: string; beoordeeld: number; bewerkt: number; bewerkt_rate: number | null; gem_gelijkenis: number | null }>;
  leerpunten: SocialLeerpunt[];
}

export type SocialActie = 'approve' | 'skip' | 'caption' | 'requeue' | 'rewrite' | 'publish_now';
export interface SocialActPayload {
  caption?: string;
  hashtags?: string[];
  reden?: string;
  /** rewrite: één zin voor de AI ("korter", "noem Joost") */
  aanwijzing?: string;
}
export interface SocialActResult {
  ok: boolean;
  id: string;
  action: SocialActie;
  status: string;
  gelijkenis?: number;
  /** rewrite: de nieuwe tekst */
  caption?: string;
  hashtags?: string[];
  /** publish_now: is de Action gestart, en wat de gebruiker moet weten */
  dispatched?: boolean;
  melding?: string;
}

interface SocialState {
  posts: SocialPost[];
  postStats: Record<string, SocialPostStats>;
  postsStatus: SocialStatus | 'alle';
  pijlerFilter: SocialPijler | null;
  stats: SocialStatsData | null;
  kandidaten: KandidatenData | null;
  weekplan: WeekplanData | null;
  bewerkingen: BewerkingenData | null;
  isLoading: boolean;
  error: string | null;

  fetchPosts: (status?: SocialStatus | 'alle', pijler?: SocialPijler | null) => Promise<void>;
  act: (id: string, action: SocialActie, payload?: SocialActPayload) => Promise<SocialActResult>;
  fetchStats: () => Promise<void>;
  fetchKandidaten: () => Promise<void>;
  fetchWeekplan: () => Promise<void>;
  fetchBewerkingen: (dagen?: number) => Promise<void>;
  actLeerpunt: (id: string, status: 'akkoord' | 'afgewezen', notitie?: string) => Promise<void>;
}

export const useSocialStore = create<SocialState>((set, get) => ({
  posts: [],
  postStats: {},
  postsStatus: 'gerenderd',
  pijlerFilter: null,
  stats: null,
  kandidaten: null,
  weekplan: null,
  bewerkingen: null,
  isLoading: false,
  error: null,

  fetchPosts: async (status, pijler) => {
    const s = status ?? get().postsStatus;
    const p = pijler === undefined ? get().pijlerFilter : pijler;
    set({ isLoading: true, error: null, postsStatus: s, pijlerFilter: p });
    try {
      const data = await marketingFetch<{ posts: SocialPost[]; stats: Record<string, SocialPostStats> }>(
        `/api/marketing/social/posts?status=${encodeURIComponent(s)}&limit=100${p ? `&pijler=${encodeURIComponent(p)}` : ''}`
      );
      set({ posts: data.posts, postStats: data.stats, isLoading: false });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Laden mislukt', isLoading: false });
    }
  },

  act: async (id, action, payload = {}) => {
    const uit = await marketingFetch<SocialActResult>('/api/marketing/social/posts', { method: 'POST', body: { id, action, ...payload } });
    // Optimistisch: rij weg uit de huidige tab (behalve caption/rewrite), rest ververst op de achtergrond.
    if (action === 'caption') {
      set({
        posts: get().posts.map((p) => (p.id === id ? { ...p, caption: payload.caption ?? p.caption, hashtags: payload.hashtags ?? p.hashtags } : p)),
      });
    } else if (action === 'rewrite') {
      set({
        posts: get().posts.map((p) => (p.id === id ? { ...p, caption: uit.caption ?? p.caption, hashtags: uit.hashtags ?? p.hashtags, caption_bewerkt_door: p.caption_bewerkt_door ?? 'kantoor' } : p)),
      });
    } else {
      set({ posts: get().posts.filter((p) => p.id !== id) });
    }
    void get().fetchPosts();
    return uit;
  },

  fetchStats: async () => {
    try {
      const stats = await marketingFetch<SocialStatsData>('/api/marketing/social/stats');
      set({ stats });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Statistieken laden mislukt' });
    }
  },

  fetchKandidaten: async () => {
    try {
      const kandidaten = await marketingFetch<KandidatenData>('/api/marketing/social/kandidaten');
      set({ kandidaten });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Kandidaten laden mislukt' });
    }
  },

  fetchWeekplan: async () => {
    try {
      const weekplan = await marketingFetch<WeekplanData>('/api/marketing/social/weekplan');
      set({ weekplan });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Weekplan laden mislukt' });
    }
  },

  fetchBewerkingen: async (dagen = 30) => {
    try {
      const bewerkingen = await marketingFetch<BewerkingenData>(`/api/marketing/social/bewerkingen?dagen=${dagen}&limit=100`);
      set({ bewerkingen });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Bewerkingen laden mislukt' });
    }
  },

  actLeerpunt: async (id, status, notitie) => {
    await marketingFetch('/api/marketing/social/weekplan', { method: 'PATCH', body: { id, status, notitie } });
    const wp = get().weekplan;
    if (wp) set({ weekplan: { ...wp, leerpunten: wp.leerpunten.filter((l) => l.id !== id) } });
  },
}));
